/**
 * Same-origin message bus between the call **host** and the call **panel**.
 *
 * WHY THIS EXISTS: embedded in Fabric, a call cannot run in the board frame at all
 * — the portal's iframe does not delegate `microphone` and Permissions Policy is
 * inherited, so an in-frame session connects and never hears the user (see
 * `@/lib/meetingCallWindow`). A popup is a top-level context and can hold the mic,
 * but making the popup the *UI* gives the user two half-surfaces: a dead
 * placeholder in the board and a real call window that drops behind it on every
 * click.
 *
 * So the popup keeps the microphone and the `RTCPeerConnection` and stops being
 * the UI. There is still exactly ONE peer connection in ONE realm; nothing about
 * WebRTC is shared. What crosses this channel is application state the Pipecat
 * callbacks have already decoded into plain JS — transcripts, booleans, a persona
 * object. Bot audio plays from the popup's own `<audio>` element, which is
 * unaffected by that window being unfocused or hidden.
 *
 * WHY `BroadcastChannel` AND NOT `postMessage`: `postMessage` needs each side to
 * hold a live handle to the other (`window.opener` / the stored popup ref). The
 * board iframe reloads routinely in Fabric — theme changes, reopening the item —
 * and every reload orphans those handles, leaving the call running behind a
 * permanently blank panel. A named bus lets either side (re)join at any time, is
 * symmetric, and does not echo to the sender.
 *
 * NO COALESCING ON PURPOSE: the host window is hidden for the whole call by
 * design, and Chrome clamps `setTimeout` in hidden windows to >=1s — then to
 * roughly once a minute under intensive throttling. A timer-based throttle would
 * turn smooth captions into 1s (then 60s) bursts. Events here are tiny, so every
 * one is posted immediately and no timer touches the hot path.
 */

import type { MicFailure } from './micDiagnostics'
import { getCallWindow } from './meetingCallWindow'

export type CallConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error'

export interface CallConversationStates {
  /** Whether the bot is awake and taking turns (the wake word, multi-agent mode, a reconnect). */
  listening: boolean
  /**
   * Whether the listen gate lets the call's audio reach the bot (/listenoff, /listenon, the
   * automatic switch around a lookup). Set only by `bot-listening-state`: the Listening control
   * shows this, never `listening`, which the wake state and a disconnect also write.
   */
  botListening: boolean
  thinking: boolean
  talking: boolean
  functionCalling: boolean
}

export interface CallPersona {
  image: string
  name: string
  role: string
}

/** Everything the panel needs to render a call. All serializable by design. */
export interface CallUiState {
  status: CallConnectionStatus
  states: CallConversationStates
  userText: string
  botText: string
  persona: CallPersona | null
  botEnabled: boolean
  micFailure: MicFailure | null
}

export const initialCallUiState: CallUiState = {
  status: 'connecting',
  states: { listening: true, botListening: true, thinking: false, talking: false, functionCalling: false },
  userText: '',
  botText: '',
  persona: null,
  botEnabled: true,
  micFailure: null,
}

/** host -> panel */
export type CallHostEvent =
  /** Full state. Sent when the host becomes ready and in reply to `hello`. */
  | { t: 'snapshot'; state: CallUiState }
  | { t: 'persona'; persona: CallPersona }
  | { t: 'transcript'; userText?: string; botText?: string }
  | { t: 'states'; patch: Partial<CallConversationStates> }
  | { t: 'status'; status: CallConnectionStatus }
  | { t: 'botEnabled'; botEnabled: boolean }
  | { t: 'mic'; micFailure: MicFailure | null }
  /** The host is going away (user closed it, or the call ended there). */
  | { t: 'bye' }

/** panel -> host */
export type CallPanelCommand =
  /** "I just mounted — send me a snapshot." */
  | { t: 'hello' }
  | { t: 'mute'; enabled: boolean }
  | { t: 'end' }

/**
 * Keyed by meeting token, not a fixed name: a stale host window from a previous
 * call would otherwise crosstalk with a new one.
 */
export const callChannelName = (token: string): string => `intui-call:${token}`

const supported = (): boolean => typeof BroadcastChannel !== 'undefined'

export interface CallChannel<TOut> {
  post: (message: TOut) => void
  close: () => void
}

/** Wire format of the `postMessage` transport; `id` dedupes it against the BroadcastChannel copy. */
interface CallEnvelope {
  intuiCall: string
  id: string
  data: unknown
}

const isEnvelope = (value: unknown, name: string): value is CallEnvelope =>
  typeof value === 'object' &&
  value !== null &&
  (value as CallEnvelope).intuiCall === name &&
  typeof (value as CallEnvelope).id === 'string'

const SEEN_LIMIT = 64

/**
 * WHY TWO TRANSPORTS: in Fabric the board is a cross-site iframe and the popup is
 * top-level. Chrome's storage partitioning keys `BroadcastChannel` by the top-level
 * site, so the two sides get DIFFERENT channels of the same name — `hello` and
 * `snapshot` never arrive and the panel sits on "Connecting…" for the whole call.
 * A direct `window.open`/`window.opener` handle is not partitioned and both sides
 * are same-origin, so it is the primary path when a peer handle exists; the
 * BroadcastChannel stays for reloads, where the handle is lost. Either may deliver
 * (or both), hence the id dedupe.
 */
function open<TIn, TOut>(
  token: string,
  onMessage: (message: TIn) => void,
  getPeer?: () => Window | null
): CallChannel<TOut> {
  const name = callChannelName(token)
  const senderId = Math.random().toString(36).slice(2)
  let seq = 0
  const seen: string[] = []
  const deliver = (id: string, data: unknown) => {
    if (seen.includes(id)) return
    seen.push(id)
    if (seen.length > SEEN_LIMIT) seen.shift()
    onMessage(data as TIn)
  }

  const channel = supported() ? new BroadcastChannel(name) : null
  if (channel) {
    channel.onmessage = (event: MessageEvent) => {
      const env = event.data
      if (isEnvelope(env, name)) deliver(env.id, env.data)
    }
  }

  const onWindowMessage = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return
    if (isEnvelope(event.data, name)) deliver(event.data.id, event.data.data)
  }
  window.addEventListener('message', onWindowMessage)

  return {
    post: (message: TOut) => {
      const envelope: CallEnvelope = { intuiCall: name, id: `${senderId}:${seq++}`, data: message }
      // A closed channel throws on post; a call ending mid-flight is normal and
      // must not surface as an error to the user.
      try {
        channel?.postMessage(envelope)
      } catch {
        /* channel already closed */
      }
      try {
        const peer = getPeer?.()
        if (peer && !peer.closed) peer.postMessage(envelope, window.location.origin)
      } catch {
        /* peer gone */
      }
    },
    close: () => {
      window.removeEventListener('message', onWindowMessage)
      if (channel) {
        channel.onmessage = null
        channel.close()
      }
    },
  }
}

/**
 * Open the channel as the call host (the popup that owns the mic and the peer
 * connection). Receives commands, posts events.
 */
export function openHostChannel(
  token: string,
  onCommand: (command: CallPanelCommand) => void
): CallChannel<CallHostEvent> {
  return open<CallPanelCommand, CallHostEvent>(token, onCommand, () => window.opener)
}

/**
 * Open the channel as the call panel (the UI in the board frame). Receives events,
 * posts commands.
 */
export function openPanelChannel(
  token: string,
  onEvent: (event: CallHostEvent) => void
): CallChannel<CallPanelCommand> {
  return open<CallHostEvent, CallPanelCommand>(token, onEvent, getCallWindow)
}

/**
 * Fold a host event into panel state.
 *
 * Pure so the reducer is testable without any windows, and so the panel cannot
 * drift from the host's semantics — `states` and `transcript` are PATCHES (a
 * transcript event carries only the field that changed), which is the one place
 * a naive `{...prev, ...event}` would silently blank the other field.
 */
export function applyCallHostEvent(prev: CallUiState, event: CallHostEvent): CallUiState {
  switch (event.t) {
    case 'snapshot':
      return event.state
    case 'persona':
      return { ...prev, persona: event.persona }
    case 'transcript':
      return {
        ...prev,
        userText: event.userText ?? prev.userText,
        botText: event.botText ?? prev.botText,
      }
    case 'states':
      return { ...prev, states: { ...prev.states, ...event.patch } }
    case 'status':
      return { ...prev, status: event.status }
    case 'botEnabled':
      return { ...prev, botEnabled: event.botEnabled }
    case 'mic':
      return { ...prev, micFailure: event.micFailure }
    case 'bye':
      return { ...prev, status: 'disconnected' }
    default:
      return prev
  }
}
